import { Component, OnInit } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { VehiclesService } from "../../../../shared/services/vehicles.service";
import { SedesService } from "../../../../shared/services/sedes.service";
import { DataService } from "../../../../shared/services/municipios.service";
import { TipoVehiculoService } from "../../../../shared/services/tipo-vehiculo.service";
import { SiniestrosService } from "../../../../shared/services/siniestrosViales.service";
import {
  VehiculoPesvPayload,
  VehiculosPesvService,
} from "../../../../shared/services/vehiculos-pesv.service";
import { Utils } from "../../../../shared/utils/Utils";
import { catchError, map } from "rxjs/operators";
import { forkJoin, of } from "rxjs";
const Swal = require("sweetalert2");

interface SedeOption {
  id: number;
  nombre_sede: string;
  ciudad_municipio: number | null;
  ciudadNombre: string;
}

interface TipoVehiculoOption {
  id: number;
  descripcion: string;
}

interface MunicipioOption {
  id: number;
  nombre: string;
}

@Component({
  selector: "app-vehiculos-pesv",
  templateUrl: './vehiculos.component.html',
  styleUrls: ['./vehiculos.component.scss'],
})
export class VehiculosPesvComponent implements OnInit {
  cargando = false;
  sedes: SedeOption[] = [];
  ciudades: string[] = [];
  municipios: MunicipioOption[] = [];
  tiposVehiculo: TipoVehiculoOption[] = [];
  vehiculos: any[] = [];
  vehiculosFiltrados: any[] = [];
  registrosPesvPorPlaca: Record<string, any> = {};
  filtro = "";
  pageSize = 10;
  page = 1;
  guardandoTodo = false;
  vehiculoModalSeleccionado: any = null;
  private ciudadIdPorNombre: Record<string, number> = {};
  private ciudadNombrePorId: Record<number, string> = {};
  private readonly estadoMap: { [key: string]: string } = {
    VIGENTE: "badge badge-success",
    "POR VENCER": "badge badge-warning",
    VENCIDO: "badge badge-danger",
    "SIN FECHA": "badge badge-light-primary",
  };

  constructor(
    private modalService: NgbModal,
    private vehiclesService: VehiclesService,
    private sedesService: SedesService,
    private dataService: DataService,
    private tipoVehiculoService: TipoVehiculoService,
    private vehiculosPesvService: VehiculosPesvService,
    private SiniestrosService: SiniestrosService
  ) {}

  ngOnInit(): void {
   this.cargarCatalogos();
    this.cargarTiposVehiculo();
    this.cargarVehiculos();
  }

  private normalizarRespuestaVehiculosPesv(respuesta: any): any[] {
    if (Array.isArray(respuesta)) {
      return respuesta;
    }

    if (Array.isArray(respuesta?.vehiculos_pesv)) {
      return respuesta.vehiculos_pesv;
    }

    if (Array.isArray(respuesta?.vehiculosPesv)) {
      return respuesta.vehiculosPesv;
    }

    if (Array.isArray(respuesta?.data)) {
      return respuesta.data;
    }

    return [];
  }

  private crearIndicePesvPorPlaca(respuesta: any): Record<string, any> {
    const lista = this.normalizarRespuestaVehiculosPesv(respuesta);
    const index: Record<string, any> = {};

    lista.forEach((item: any) => {
      const placa = String(item?.placa || "").trim().toUpperCase();
      if (placa) {
        index[placa] = item;
      }
    });

    return index;
  }

  private inicializarDatosPesvEnVehiculo(vehiculo: any): any {
    const placa = String(vehiculo?.placa || "").trim().toUpperCase();
    const registro = this.registrosPesvPorPlaca[placa];

    return {
      ...vehiculo,
      _pesv: registro || null,
      id_vehiculo_pesv: Number(registro?.id || 0) || null,
      guardando: false,
      numero_vin: Number(vehiculo?.numero_vin ?? registro?.numero_vin ?? 0) || 0,
      fecha_fabricacion: vehiculo?.fecha_fabricacion || registro?.fecha_fabricacion || "",
      tarjeta_operacion:
        Number(
          vehiculo?.tarjeta_operacion ??
            vehiculo?.tarjetasOperaciones?.[0]?.numero_tarjeta ??
            registro?.tarjeta_operacion ??
            0
        ) || 0,
      promedio_kilometraje:
        Number(vehiculo?.promedio_kilometraje ?? registro?.promedio_kilometraje ?? 0) || 0,
      cod_infracion: Number(vehiculo?.cod_infracion ?? registro?.cod_infracion ?? 0) || 0,
      ultima_revision: vehiculo?.ultima_revision || registro?.ultima_revision || "",
      fecha_siniestros: vehiculo?.fecha_siniestros || registro?.fecha_siniestros || "",
      lugar_siniestros: vehiculo?.lugar_siniestros || registro?.lugar_siniestros || "",
      area_rural: Number(vehiculo?.area_rural ?? registro?.area_rural ?? 0) || 0,
    };
  }

  private cargarTiposVehiculo(): void {
    this.tipoVehiculoService
      .getAll()
      .pipe(catchError(() => of([])))
      .subscribe((respuesta: any) => {
        this.tiposVehiculo = this.normalizarRespuestaTiposVehiculo(respuesta);
      });
  }

  private normalizarRespuestaTiposVehiculo(respuesta: any): TipoVehiculoOption[] {
    const lista = Array.isArray(respuesta)
      ? respuesta
      : Array.isArray(respuesta?.tipos)
      ? respuesta.tipos
      : Array.isArray(respuesta?.tipoVehiculos)
      ? respuesta.tipoVehiculos
      : Array.isArray(respuesta?.data)
      ? respuesta.data
      : [];

    return lista
      .map((item: any) => ({
        id: Number(item?.id || 0),
        descripcion: String(item?.descripcion || "").trim(),
      }))
      .filter((item: TipoVehiculoOption) => !!item.id && !!item.descripcion);
  }

  private cargarCatalogos(): void {
    forkJoin({
      sedes: this.sedesService.getAll().pipe(catchError(() => of([]))),
      municipios: this.dataService.getDatos().pipe(catchError(() => of([]))),
    }).subscribe({
      next: ({ sedes, municipios }) => {
        const indexMunicipios = this.crearIndiceMunicipios(municipios);
        this.ciudades = this.normalizarRespuestaMunicipios(municipios);
        this.sedes = this.normalizarRespuestaSedes(sedes, indexMunicipios);
      },
      error: () => {
        this.sedes = [];
        this.ciudades = [];
      },
    });
  }

  private normalizarRespuestaMunicipios(respuesta: any): string[] {
    const lista = Array.isArray(respuesta) ? respuesta : [];
    this.ciudadIdPorNombre = {};
    this.ciudadNombrePorId = {};
    this.municipios = [];

    const nombres = lista
      .map((item: any) => {
        const nombre = String(item?.MUNICIPIO || "").trim();
        const id = Number(item?.IDMUN || 0);
        if (nombre && id) {
          this.ciudadIdPorNombre[nombre.toUpperCase()] = id;
          this.ciudadNombrePorId[id] = nombre;
          this.municipios.push({ id, nombre });
        }

        return nombre;
      })
      .filter((item: string) => !!item);
    return Array.from(new Set(nombres));
  }

  private crearIndiceMunicipios(respuesta: any): Record<number, string> {
    const lista = Array.isArray(respuesta) ? respuesta : [];
    const index: Record<number, string> = {};
    lista.forEach((item: any) => {
      const id = Number(item?.IDMUN || 0);
      if (!id) {
        return;
      }

      const nombre = String(item?.MUNICIPIO || "").trim();
      if (nombre) {
        index[id] = nombre;
      }
    });

    return index;
  }

  private normalizarRespuestaSedes(
    respuesta: any,
    indexMunicipios: Record<number, string>
  ): SedeOption[] {
    const lista = Array.isArray(respuesta)
      ? respuesta
      : Array.isArray(respuesta?.sedes)
      ? respuesta.sedes
      : Array.isArray(respuesta?.data)
      ? respuesta.data
      : [];

    return lista.map((item: any) => ({
      id: Number(item?.id || 0),
      nombre_sede: String(item?.nombre_sede || "").trim(),
      ciudad_municipio: Number(item?.ciudad_municipio ?? item?.ciudad ?? 0) || null,
      ciudadNombre: this.resolverNombreCiudadSede(item, indexMunicipios),
    }));
  }

  private resolverNombreCiudadSede(item: any, indexMunicipios: Record<number, string>): string {
    const idMunicipio = Number(item?.ciudad_municipio ?? item?.ciudad ?? 0);
    if (idMunicipio && indexMunicipios[idMunicipio]) {
      return indexMunicipios[idMunicipio];
    }

    const ciudadTexto = String(item?.ciudad || "").trim();
    return ciudadTexto;
  }

  cargarVehiculos(): void {
    this.cargando = true;
    forkJoin({
      vehiculos: this.vehiclesService.getAll(),
      vehiculosPesv: this.vehiculosPesvService.getAll().pipe(catchError(() => of([]))),
    }).subscribe({
      next: ({ vehiculos, vehiculosPesv }) => {
        this.registrosPesvPorPlaca = this.crearIndicePesvPorPlaca(vehiculosPesv);
        this.vehiculos = this.normalizarRespuestaVehiculos(vehiculos).map((item: any) =>
          this.inicializarDatosPesvEnVehiculo(item)
        );
        this.aplicarFiltro();
        this.cargando = false;
      },
      error: (error) => {
        console.error("Error al cargar vehiculos PESV:", error);
        this.vehiculos = [];
        this.vehiculosFiltrados = [];
        this.cargando = false;
      },
    });
  }

  private normalizarRespuestaVehiculos(respuesta: any): any[] {
    if (Array.isArray(respuesta)) {
      return respuesta;
    }

    if (Array.isArray(respuesta?.vehicles)) {
      return respuesta.vehicles;
    }

    if (Array.isArray(respuesta?.data)) {
      return respuesta.data;
    }

    return [];
  }

  aplicarFiltro(): void {
    const termino = (this.filtro || "").trim().toLowerCase();
    this.page = 1;

    if (!termino) {
      this.vehiculosFiltrados = [...this.vehiculos];
      return;
    }

    this.vehiculosFiltrados = this.vehiculos.filter((vehiculo) => {
      const propietario = [
        vehiculo?.propietario?.primernombre,
        vehiculo?.propietario?.segundonombre,
        vehiculo?.propietario?.papellidos,
        vehiculo?.propietario?.seapellidos,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return (
        String(vehiculo?.ciudad || "").toLowerCase().includes(termino) ||
        String(vehiculo?.placa || "").toLowerCase().includes(termino) ||
        String(vehiculo?.interno || "").toLowerCase().includes(termino) ||
        String(vehiculo?.sede || "").toLowerCase().includes(termino) ||
        String(vehiculo?.marcas?.marca || "").toLowerCase().includes(termino) ||
        String(vehiculo?.modelo || "").toLowerCase().includes(termino) ||
        String(vehiculo?.tipoVehiculo?.tipos || "").toLowerCase().includes(termino) ||
        String(vehiculo?.tipoRelacion?.relacion || "").toLowerCase().includes(termino) ||
        propietario.includes(termino)
      );
    });
  }

  nombrePropietario(vehiculo: any): string {
    return [
      vehiculo?.propietario?.primernombre,
      vehiculo?.propietario?.segundonombre,
      vehiculo?.propietario?.papellidos,
      vehiculo?.propietario?.seapellidos,
    ]
      .filter(Boolean)
      .join(" ");
  }

  estadoTexto(estado: any): string {
    return Number(estado) === 1 ? "ACTIVO" : "INACTIVO";
  }

  formatearFecha(valor: any): string {
    if (!valor) {
      return "-";
    }

    const fecha = new Date(valor);
    if (Number.isNaN(fecha.getTime())) {
      return "-";
    }

    const dia = String(fecha.getDate()).padStart(2, "0");
    const mes = String(fecha.getMonth() + 1).padStart(2, "0");
    const anio = fecha.getFullYear();
    return `${dia}/${mes}/${anio}`;
  }

  private normalizarEstado(estado: string | undefined): string {
    const valor = String(estado || "").toUpperCase().trim();

    if (!valor) {
      return "SIN FECHA";
    }

    if (valor.includes("POR") && valor.includes("VENC")) {
      return "POR VENCER";
    }

    if (valor.includes("VENC")) {
      return "VENCIDO";
    }

    if (valor.includes("VIGENT") || valor.includes("ACTIV")) {
      return "VIGENTE";
    }

    if (valor.includes("SIN FECHA") || valor === "SINFECHA") {
      return "SIN FECHA";
    }

    return valor;
  }

  getEstadoColor(estado: string | undefined): string {
    const estadoNormalizado = this.normalizarEstado(estado);
    return this.estadoMap[estadoNormalizado] || "badge badge-light-primary";
  }

  private calcularEstadoPorFecha(fechaValor: any): string {
    if (!fechaValor) {
      return "SIN FECHA";
    }

    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    const fecha = new Date(fechaValor);
    if (Number.isNaN(fecha.getTime())) {
      return "SIN FECHA";
    }
    fecha.setHours(0, 0, 0, 0);

    const diffDias = Math.ceil((fecha.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDias < 0) {
      return "VENCIDO";
    }

    if (diffDias <= 30) {
      return "POR VENCER";
    }

    return "VIGENTE";
  }

  obtenerEstadoSoat(vehiculo: any): string {
    const soatVigente = vehiculo?.historialSoat?.find(
      (registroSoat: any) => this.normalizarEstado(registroSoat?.estadoCalculado?.estado) === "VIGENTE"
    );

    return (
      soatVigente?.estadoCalculado?.estado ||
      vehiculo?._pesv?.estado_soat ||
      vehiculo?.historialSoat?.[0]?.estadoCalculado?.estado ||
      this.calcularEstadoPorFecha(vehiculo?.historialSoat?.[0]?.vencimiento_soat)
    );
  }

 obtenerEstadoRc(vehiculo: any): string {
  const contratoVigente = vehiculo?.historialContractual?.find(
    (contrato: any) =>
      this.normalizarEstado(contrato?.estadoCalculado?.estado) === "VIGENTE"
  );

  return contratoVigente?.estadoCalculado?.estado || "SIN INFORMACIÓN";
}

  obtenerEstadoTecnomecanica(vehiculo: any): string {
    const tecnomecanicaVigente = vehiculo?.historialCda?.find(
      (registroCda: any) => this.normalizarEstado(registroCda?.estadoCalculado?.estado) === "VIGENTE"
    );

    return (
      tecnomecanicaVigente?.estadoCalculado?.estado ||
      vehiculo?._pesv?.estado_tecnomecanica ||
      vehiculo?.historialCda?.[0]?.estadoCalculado?.estado ||
      this.calcularEstadoPorFecha(vehiculo?.historialCda?.[0]?.vcda)
    );
  }

  obtenerEstadoMantenimiento(vehiculo: any): string {
    return (
      vehiculo?.estadoUltimoMantenimiento?.estado ||
      vehiculo?._pesv?.estado_mantenimiento ||
      this.calcularEstadoPorFecha(vehiculo?.estadoUltimoMantenimiento?.fecha) ||
      "SIN FECHA"
    );
  }

  obtenerVencimientoSoat(vehiculo: any): any {
    const soatVigente = vehiculo?.historialSoat?.find(
      (registroSoat: any) => this.normalizarEstado(registroSoat?.estadoCalculado?.estado) === "VIGENTE"
    );

    return (
      soatVigente?.vencimiento_soat ||
      vehiculo?._pesv?.vencimiento_soat ||
      vehiculo?.historialSoat?.[0]?.vencimiento_soat
    );
  }

  obtenerVencimientoRc(vehiculo: any): any {
    const contratoVigente = vehiculo?.historialContractual?.find(
      (contrato: any) => this.normalizarEstado(contrato?.estadoCalculado?.estado) === "VIGENTE"
    );
    //console.log(contratoVigente?.estadoCalculado?.estado);
    return (
      contratoVigente?.fecha_vencimientoRCC ||
      vehiculo?._pesv?.vencimiento_rc ||
      vehiculo?.historialContractual?.[0]?.fecha_vencimientoRCC
    );
  }

  obtenerVencimientoTecnomecanica(vehiculo: any): any {
    const tecnomecanicaVigente = vehiculo?.historialCda?.find(
      (registroCda: any) => this.normalizarEstado(registroCda?.estadoCalculado?.estado) === "VIGENTE"
    );

    return (
      tecnomecanicaVigente?.vcda ||
      vehiculo?._pesv?.vencimentio_tecnomecanica ||
      vehiculo?.historialCda?.[0]?.vcda
    );
  }

  obtenerVencimientoMantenimiento(vehiculo: any): any {
    return (
      vehiculo?.estadoUltimoMantenimiento?.fecha ||
      vehiculo?._pesv?.vencimiento_mantenimiento
    );
  }

  private obtenerFechaIso(valor: any): string {
    const fecha = new Date(valor || new Date());
    if (Number.isNaN(fecha.getTime())) {
      const hoy = new Date();
      return hoy.toISOString().slice(0, 10);
    }

    return fecha.toISOString().slice(0, 10);
  }

  private aNumero(valor: any): number {
    const num = Number(valor);
    return Number.isFinite(num) ? num : 0;
  }

  private obtenerIdCiudad(nombreCiudad: any): number {
    const nombre = String(nombreCiudad || "").trim().toUpperCase();
    return this.ciudadIdPorNombre[nombre] || 0;
  }

  private obtenerIdSede(nombreSede: any): number {
    const nombre = String(nombreSede || "").trim();
    const sede = this.sedes.find((item) => item.nombre_sede === nombre);
    return sede?.id || 0;
  }

  private construirPayloadVehiculoPesv(vehiculo: any): VehiculoPesvPayload {
    const registro = vehiculo?._pesv || {};
    const estadoMantenimiento = this.normalizarEstado(this.obtenerEstadoMantenimiento(vehiculo));

    return {
      placa: String(vehiculo?.placa || "").trim(),
      ciudad: this.obtenerIdCiudad(vehiculo?.ciudad) || this.aNumero(registro?.ciudad),
      sede: this.obtenerIdSede(vehiculo?.sede) || this.aNumero(registro?.sede),
      fecha_ingreso: this.obtenerFechaIso(vehiculo?.afiliacion),
      numero_vin: this.aNumero(vehiculo?.numero_vin ?? registro?.numero_vin),
      fecha_fabricacion: this.obtenerFechaIso(vehiculo?.fecha_fabricacion || registro?.fecha_fabricacion),
      motor: this.aNumero(vehiculo?.motor ?? registro?.motor),
      licencia: this.aNumero(vehiculo?.matricula ?? registro?.licencia),
      tarjeta_operacion: this.aNumero(
        vehiculo?.tarjeta_operacion ??
          vehiculo?.tarjetasOperaciones?.[0]?.numero_tarjeta ??
          registro?.tarjeta_operacion
      ),
      kilometraje: this.aNumero(vehiculo?.kilometraje ?? registro?.kilometraje),
      promedio_kilometraje: this.aNumero(vehiculo?.promedio_kilometraje),
      clase: String(vehiculo?.tipoVehiculo?.tipos || registro?.clase || "").trim(),
      tipo: this.aNumero(this.obtenerTipoVehiculoSeleccionado(vehiculo) ?? registro?.tipo),
      marca: String(vehiculo?.marcas?.marca || registro?.marca || "").trim(),
      modelo: this.aNumero(vehiculo?.modelo ?? registro?.modelo),
      color: String(vehiculo?.color || registro?.color || "").trim(),
      linea: String(vehiculo?.linea || registro?.linea || "").trim(),
      matricula: this.aNumero(vehiculo?.matricula ?? registro?.matricula),
      propietario: String(this.nombrePropietario(vehiculo) || registro?.propietario || "").trim(),
      cedula: this.aNumero(vehiculo?.propietario?.cedula ?? registro?.cedula),
      contacto: this.aNumero(vehiculo?.propietario?.telcel ?? registro?.contacto),
      empresa: String(vehiculo?.nombreEmpresa?.nombre || registro?.empresa || "").trim(),
      contacto_empresa: this.aNumero(vehiculo?.nombreEmpresa?.celular ?? registro?.contacto_empresa),
      cod_infracion: this.aNumero(vehiculo?.cod_infracion),
      ultima_revision: this.obtenerFechaIso(vehiculo?.ultima_revision || registro?.ultima_revision),
      fecha_siniestros: this.obtenerFechaIso(vehiculo?.fecha_siniestros || registro?.fecha_siniestros),
      lugar_siniestros: String(vehiculo?.lugar_siniestros || registro?.lugar_siniestros || "").trim(),
      area_rural: this.aNumero(vehiculo?.area_rural ?? registro?.area_rural),
      vencimiento_soat: this.obtenerFechaIso(
        this.obtenerVencimientoSoat(vehiculo) || registro?.vencimiento_soat
      ),
      estado_soat: this.normalizarEstado(this.obtenerEstadoSoat(vehiculo)),
      vencimiento_rc: this.obtenerFechaIso(this.obtenerVencimientoRc(vehiculo) || registro?.vencimiento_rc),
      estrado_rc: this.normalizarEstado(this.obtenerEstadoRc(vehiculo)),
      vencimentio_tecnomecanica: this.obtenerFechaIso(
        this.obtenerVencimientoTecnomecanica(vehiculo) || registro?.vencimentio_tecnomecanica
      ),
      estado_tecnomecanica: this.normalizarEstado(this.obtenerEstadoTecnomecanica(vehiculo)),
      vencimiento_mantenimiento: this.obtenerFechaIso(
        this.obtenerVencimientoMantenimiento(vehiculo) || registro?.vencimiento_mantenimiento
      ),
      estado_mantenimiento: estadoMantenimiento || this.normalizarEstado(registro?.estado_mantenimiento),
      estado: this.aNumero(vehiculo?.estado ?? registro?.estado) || 1,
    };
  }

  abrirModalDiagnosticoVehiculo(content: any, vehiculo: any): void {
 //   console.log("Abrir modal diagnostico vehiculo:", vehiculo);
    this.vehiculoModalSeleccionado = vehiculo;

   this.SiniestrosService.getSiniestroByPlaca(vehiculo.placa).subscribe({
  next: (siniestros: any[]) => {
    this.vehiculoModalSeleccionado.siniestros = siniestros ?? [];
  },
  error: (error) => {
    console.error("Error al obtener siniestros:", error);
    this.vehiculoModalSeleccionado.siniestros = [];
  }
});

    this.modalService.open(content, {
      size: "xl",
      centered: true,
      backdrop: "static",
    });
  }

  guardarVehiculoDesdeModal(modal: any): void {
    if (!this.vehiculoModalSeleccionado) {
      Swal.fire("Advertencia", "No hay vehiculo seleccionado.", "warning");
      return;
    }

    this.guardarVehiculoPesv(this.vehiculoModalSeleccionado, () => modal.close());
  }

  guardarVehiculoPesv(vehiculo: any, onFinish?: () => void): void {
    const payload = this.construirPayloadVehiculoPesv(vehiculo);
    const id = Number(vehiculo?.id_vehiculo_pesv || 0);
    const request$ = id
      ? this.vehiculosPesvService.update(id, payload)
      : this.vehiculosPesvService.create(payload);

    vehiculo.guardando = true;

    request$.subscribe({
      next: (resp: any) => {
        vehiculo.guardando = false;
        vehiculo._pesv = {
          ...(vehiculo?._pesv || {}),
          ...payload,
        };
        if (!id) {
          const idCreado = Number(resp?.id || resp?.vehiculo_pesv?.id || 0);
          if (idCreado) {
            vehiculo.id_vehiculo_pesv = idCreado;
            vehiculo._pesv.id = idCreado;
          }
        }

        Swal.fire({
          icon: "success",
          title: "Guardado",
          text: id
            ? `Vehiculo ${payload.placa} actualizado exitosamente`
            : `Vehiculo ${payload.placa} guardado exitosamente`,
          timer: 1500,
          showConfirmButton: false,
        });
        onFinish?.();
      },
      error: (error: any) => {
        vehiculo.guardando = false;
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error?.error?.message || "No se pudo guardar el vehiculo PESV",
        });
        onFinish?.();
      },
    });
  }

  guardarTodoVehiculosPesv(): void {
    const lista = Array.isArray(this.vehiculosFiltrados) ? this.vehiculosFiltrados : [];

    if (!lista.length) {
      Swal.fire({
        icon: "info",
        title: "Sin registros",
        text: "No hay vehiculos para guardar.",
      });
      return;
    }

    if (this.guardandoTodo) {
      return;
    }

    this.guardandoTodo = true;
    const peticiones = lista.map((vehiculo) => {
      const payload = this.construirPayloadVehiculoPesv(vehiculo);
      const id = Number(vehiculo?.id_vehiculo_pesv || 0);
      vehiculo.guardando = true;

      const request$ = id
        ? this.vehiculosPesvService.update(id, payload)
        : this.vehiculosPesvService.create(payload);

      return request$.pipe(
        map((resp: any) => ({ ok: true, resp, vehiculo })),
        catchError((error) =>
          of({
            __error: true,
            error,
            placa: payload.placa,
            vehiculo,
          })
        )
      );
    });

    forkJoin(peticiones).subscribe({
      next: (resultados: any[]) => {
        resultados.forEach((item: any) => {
          const vehiculo = item?.vehiculo;
          if (vehiculo) {
            vehiculo.guardando = false;
          }
        });

        const resumen = resultados.reduce(
          (acc: { exitosos: number; errores: number }, item: any) => {
            const vehiculo = item?.vehiculo;
            if (item?.__error || item?.ok === false) {
              acc.errores += 1;
              return acc;
            }

            if (vehiculo && !vehiculo.id_vehiculo_pesv) {
              const idCreado = Number(item?.resp?.id || item?.resp?.vehiculo_pesv?.id || 0);
              if (idCreado) {
                vehiculo.id_vehiculo_pesv = idCreado;
              }
            }

            if (vehiculo) {
              const payloadGuardado = this.construirPayloadVehiculoPesv(vehiculo);
              vehiculo._pesv = {
                ...(vehiculo?._pesv || {}),
                ...payloadGuardado,
                id: vehiculo.id_vehiculo_pesv || vehiculo?._pesv?.id,
              };
            }

            acc.exitosos += 1;
            return acc;
          },
          { exitosos: 0, errores: 0 }
        );

        this.guardandoTodo = false;
        const textoResultado = resumen.errores
          ? `Guardados: ${resumen.exitosos} | Errores: ${resumen.errores}`
          : `Guardados: ${resumen.exitosos}`;

        Swal.fire({
          icon: resumen.errores ? "warning" : "success",
          title: resumen.errores ? "Guardado parcial" : "Guardado completado",
          text: textoResultado,
        });
      },
      error: () => {
        lista.forEach((vehiculo: any) => {
          vehiculo.guardando = false;
        });
        this.guardandoTodo = false;
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo completar el guardado masivo.",
        });
      },
    });
  }


  esActivo(estado: any): boolean {
    return Number(estado) === 1;
  }

  obtenerNombreSedeActual(vehiculo: any): string | null {
    let sedeTexto = String(vehiculo?.sede || "").trim();
    if (!sedeTexto) {
      const sedeId = Number(vehiculo?._pesv?.sede || 0);
      if (sedeId) {
        const sede = this.sedes.find((item) => item.id === sedeId);
        sedeTexto = String(sede?.nombre_sede || "").trim();
      }
    }

    return sedeTexto || null;
  }

  actualizarSedeVehiculo(vehiculo: any, sedeNombre: string): void {
    vehiculo.sede = String(sedeNombre || "").trim();
  }

  obtenerCiudadActual(vehiculo: any): string | null {
    let ciudad = String(vehiculo?.ciudad || "").trim();
    if (!ciudad) {
      const ciudadId = Number(vehiculo?._pesv?.ciudad || 0);
      if (ciudadId) {
        ciudad = String(this.ciudadNombrePorId[ciudadId] || "").trim();
      }
    }

    return ciudad || null;
  }

  actualizarCiudadVehiculo(vehiculo: any, ciudad: string): void {
    vehiculo.ciudad = String(ciudad || "").trim();
  }

  obtenerAreaRuralSeleccionada(vehiculo: any): number | null {
    const id = Number(vehiculo?.area_rural || 0);
    return id || null;
  }

  actualizarAreaRuralVehiculo(vehiculo: any, areaRural: number): void {
    vehiculo.area_rural = Number(areaRural || 0) || 0;
  }

  obtenerTipoVehiculoSeleccionado(vehiculo: any): number | null {
    const id = Number(
      vehiculo?.tipo_vehiculo_id ?? vehiculo?.clase ?? vehiculo?.tipoVehiculo?.id ?? 0
    );
    return id || null;
  }

  actualizarTipoVehiculo(vehiculo: any, tipoVehiculoId: number): void {
    const id = Number(tipoVehiculoId || 0) || null;
    const tipoSeleccionado = this.tiposVehiculo.find((tipo) => tipo.id === id);

    vehiculo.tipo_vehiculo_id = id;
    vehiculo.clase = id;

    if (tipoSeleccionado) {
      vehiculo.tipoVehiculo = {
        ...(vehiculo.tipoVehiculo || {}),
        id: tipoSeleccionado.id,
        tipos: tipoSeleccionado.descripcion,
      };
    }
  }

  exportToPDF(tableId: string, nameFile: string, titleFile: string): void {
    Utils.exportToPDF(tableId, nameFile, titleFile);
  }

  exportarExcel(tableId: string, nameFile: string): void {
    Utils.exportVisibleTableToExcel(tableId, nameFile);
  }
}
